import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Flame, Dumbbell, Heart, Timer } from "lucide-react";
import Header from "@/components/Header";

interface Workout {
  name: string;
  duration: number;
  calories: number;
  intensity: "Low" | "Medium" | "High";
  exercises: string[];
}

interface DayPlan {
  day: string;
  focus: string;
  completed: boolean;
  today: boolean;
}

const Fitness = () => {
  const [age, setAge] = useState<number | "">("");
  const [bmi, setBmi] = useState<number | "">("");
  const [gender, setGender] = useState<string>("");
  const [activity, setActivity] = useState<string>("");
  const [goal, setGoal] = useState<string>("");

  const [todaysWorkout, setTodaysWorkout] = useState<Workout[]>([]);
  const [weeklyPlan, setWeeklyPlan] = useState<DayPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!age || !bmi || !gender || !activity || !goal) {
      setError("Please fill all fields.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch("https://tobex.com.ng/hms/python/fitness/plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ age, bmi, gender, activity, goal }),
      });

      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setTodaysWorkout(data.todaysWorkout || []);
        setWeeklyPlan(data.weeklyPlan || []);
      }
    } catch (err: any) {
      setError(
        err.message ||
          "Failed to fetch fitness plan. Make sure the backend server is running."
      );
    } finally {
      setLoading(false);
    }
  };

  const totalDuration = todaysWorkout.reduce((sum, w) => sum + w.duration, 0);
  const totalCalories = todaysWorkout.reduce((sum, w) => sum + w.calories, 0);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Your Dynamic Fitness Plan
          </h1>
          <p className="text-muted-foreground">
            Intelligent workout routines designed to help you reach your peak
            performance.
          </p>
        </div>

        {/* User Input Form */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          {[
            { label: "Age", value: age, setter: setAge, type: "number" },
            { label: "BMI", value: bmi, setter: setBmi, type: "number" },
          ].map((field) => (
            <div key={field.label} className="flex flex-col">
              <label className="text-sm font-medium text-muted-foreground">
                {field.label}
              </label>
              <input
                type={field.type}
                placeholder={field.label}
                value={field.value}
                onChange={(e) =>
                  field.setter(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          ))}

          {[
            { label: "Gender", value: gender, setter: setGender, options: ["male", "female"] },
            { label: "Activity", value: activity, setter: setActivity, options: ["low", "moderate", "high"] },
            { label: "Goal", value: goal, setter: setGoal, options: ["weight_loss", "maintain", "muscle_gain"] },
          ].map((field) => (
            <div key={field.label} className="flex flex-col">
              <label className="text-sm font-medium text-muted-foreground">
                {field.label}
              </label>
              <select
                value={field.value}
                onChange={(e) => field.setter(e.target.value)}
                className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="" disabled>
                  Select {field.label}
                </option>
                {field.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <div className="flex flex-col">
            <Button onClick={handleSubmit} className="mt-2 md:mt-6">
              {loading ? "Loading..." : "Generate Plan"}
            </Button>
          </div>
        </div>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* Workout Summary */}
        {todaysWorkout.length > 0 && (
          <div className="mb-8 animate-scale-in">
            <Card className="border shadow-sm">
              <CardHeader>
                <CardTitle className="text-2xl">Today's Training Session</CardTitle>
                <CardDescription>
                  A balanced workout combining strength, cardio, and core exercises.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-muted">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Timer className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">{totalDuration} Mins</div>
                      <p className="text-xs text-muted-foreground">Total Duration</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-muted">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Flame className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">~{totalCalories}</div>
                      <p className="text-xs text-muted-foreground">Calories Burned</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-muted">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Heart className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-xl font-bold">Balanced</div>
                      <p className="text-xs text-muted-foreground">Intensity</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Today's Exercises */}
        {todaysWorkout.map((workout, index) => (
          <Card
            key={index}
            className="rounded-2xl border-0 shadow-sm hover:shadow-lg transition-all card-hover mb-4"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                    <Dumbbell className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{workout.name}</CardTitle>
                    <div className="flex gap-2 mt-1">
                      <Badge
                        variant={
                          workout.intensity === "High"
                            ? "destructive"
                            : workout.intensity === "Medium"
                            ? "default"
                            : "secondary"
                        }
                      >
                        {workout.intensity} Intensity
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">{workout.duration} min</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Flame className="h-4 w-4" />
                    <span className="text-sm">~{workout.calories} cal</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {workout.exercises.map((exercise, exIndex) => (
                  <li
                    key={exIndex}
                    className="flex items-center gap-2 p-3 rounded-lg bg-muted/30"
                  >
                    <Heart className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{exercise}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}

        {/* Weekly Plan */}
        {weeklyPlan.length > 0 && (
          <div className="mb-8 animate-slide-up">
            <h2 className="text-2xl font-semibold mb-4">Your Weekly Training Schedule</h2>
            <Card className="border shadow-sm">
              <CardContent className="pt-6">
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  {weeklyPlan.map((day, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border transition-all ${
                        day.today
                          ? "border-primary bg-primary/5"
                          : day.completed
                          ? "border-green-500/30 bg-green-500/5"
                          : "border-border bg-muted/20"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm">{day.day}</span>
                        {day.completed && (
                          <Badge
                            variant="outline"
                            className="text-green-600 border-green-600"
                          >
                            Completed
                          </Badge>
                        )}
                        {day.today && (
                          <Button variant="secondary" size="sm">
                            Log Now
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{day.focus}</p>
                      {!day.completed && !day.today && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {day.day} · 45 mins
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Start Workout Button */}
        {todaysWorkout.length > 0 && (
          <div className="text-center animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <Button size="lg" className="px-8">
              Log Today's Workout
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fitness;
